/* eslint-disable react/require-default-props */
import React from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  useTheme,
  useMediaQuery,
} from '@material-ui/core';
import {
  makeStyles,
  withStyles,
  Theme,
} from '@material-ui/core/styles';
import StepConnector from '@material-ui/core/StepConnector';
import {
  StepIconProps,
} from '@material-ui/core/StepIcon';

import {
  AssignmentInd,
  Description,
  Print,
  HowToReg,
} from '@material-ui/icons';
import {
  MdCancel,
  MdContentPaste,
  MdPersonOff,
  MdQrCode,
  MdScheduleSend,
} from 'react-icons/md';
import { IoMdAlert } from 'react-icons/io';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: '100%',
    backgroundColor: '#fff',
    padding: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
    },
  },
  stepper: {
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1, 0),
      '& .MuiStepConnector-vertical': {
        marginLeft: 24,
        minHeight: 40,
      },
      '& .MuiStepLabel-vertical': {
        padding: theme.spacing(0, 0, 2, 0),
      },
      '& .MuiStep-vertical': {
        paddingLeft: 20,
      },
      '& .MuiStepContent-root': {
        marginLeft: 24,
        paddingLeft: theme.spacing(2),
      },
    },
  },
  stepLabel: {
    color: '#616161',
    '&.MuiStepLabel-active': {
      color: '#616161',
    },
    '&.MuiStepLabel-completed': {
      color: '#616161',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.85rem',
      paddingLeft: theme.spacing(1),
    },
  },
  cancelMessage: {
    textAlign: 'center',
    marginTop: theme.spacing(2),
    fontWeight: 'bold',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.9rem',
    },
  },
  stepContainer: {
    [theme.breakpoints.down('xs')]: {
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      marginBottom: theme.spacing(2),
    },
  },
}));

const useColorlibStepIconStyles = makeStyles(() => ({
  root: {
    backgroundColor: '#E0E0E0',
    zIndex: 1,
    color: '#B3B3B3',
    width: ({ isMobile }: { isMobile: boolean }) => (isMobile ? 50 : 80),
    height: ({ isMobile }: { isMobile: boolean }) => (isMobile ? 50 : 80),
    display: 'flex',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
    '& svg': {
      fontSize: ({ isMobile }: { isMobile: boolean }) => (isMobile ? '1.5rem' : '2rem'),
    },
  },
  completed: {
    backgroundColor: '#4CAF50',
    color: '#fff',
  },
  lastStepCancelled: {
    backgroundColor: '#FF0000',
    color: '#fff',
  },
}));

const ColorlibConnector = withStyles((theme: Theme) => ({
  alternativeLabel: {
    top: 35,
    [theme.breakpoints.down('sm')]: {
      top: 22,
    },
  },
  vertical: {
    marginLeft: ({ isMobile }: { isMobile: boolean }) => (isMobile ? 25 : 'auto'),
    padding: 0,
    '& $line': {
      minHeight: 40,
    },
  },
  active: {
    '& $line': {
      backgroundColor: '#4CAF50',
    },
  },
  completed: {
    '& $line': {
      backgroundColor: '#4CAF50',
    },
  },
  line: {
    height: ({ isMobile }: { isMobile: boolean }) => (isMobile ? '100%' : 12),
    width: ({ isMobile }: { isMobile: boolean }) => (isMobile ? 3 : 'auto'),
    border: 0,
    borderRadius: 1,
    backgroundColor: (props: any) => {
      if (props.isFirstLine) return '#4CAF50';
      if (props.isCancelled || props.isBlocked) return '#FF0000';
      return '#D9D9D9';
    },
  },
}))(StepConnector);

const stepData = [
  'Processo iniciado',
  'Em análise',
  'Em processo de validação do CPF',
  'Em processo de validação do MJ',
  'Em processo de emissão',
  'CIN a caminho do posto de atendimento',
  'Aguardando retirada da CIN',
  'CIN retirado com sucesso',
];

const stepIcons: { [key: number]: React.ReactElement } = {
  1: <Description />,
  2: <MdContentPaste />,
  3: <AssignmentInd />,
  4: <MdQrCode />,
  5: <Print />,
  6: <MdScheduleSend />,
  7: <IoMdAlert />,
  8: <HowToReg />,
};

const createConditionalClassName = (baseClass: string, conditions:
{ [key: string]: boolean }): string => {
  const classes = [baseClass];

  Object.entries(conditions).forEach(([className, shouldApply]) => {
    if (shouldApply) {
      classes.push(className);
    }
  });

  return classes.join(' ');
};

const ColorlibStepIcon: React.FC<StepIconProps & {
  isCancelled?: boolean,
  isBlocked?: boolean,
  isLastStep?: boolean
}> = ({
  completed,
  icon,
  isCancelled,
  isBlocked,
  isLastStep,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const classes = useColorlibStepIconStyles({ isMobile });

  const getLastStepIcon = () => {
    if (isCancelled) return <MdCancel />;
    if (isBlocked) return <MdPersonOff />;
    return stepIcons[Number(icon)];
  };

  const rootClassName = createConditionalClassName(classes.root, {
    [classes.completed]: !isCancelled && completed,
    [classes.lastStepCancelled]: (isCancelled || isBlocked) && isLastStep,
  });

  return (
    <div className={rootClassName}>
      {isLastStep && (isCancelled || isBlocked)
        ? getLastStepIcon()
        : stepIcons[Number(icon)]}
    </div>
  );
};

interface ServiceProgressCardProps {
  statusProcesso: string;
}

export default function ServiceProgressCard({
  statusProcesso,
}: ServiceProgressCardProps): JSX.Element {
  const determineActiveStep = () => {
    // Função para normalizar strings: remove Unicode, deixa em minúsculas
    const normalizeStatus = statusProcesso.toLowerCase();

    const statusMap: { [key: string]: { step: number, type?: 'cancelled' | 'blocked' } } = {
      // Status: Em análise
      'em analise': { step: 2 },
      'em análise': { step: 2 },
      'em triagem': { step: 2 },
      'divergência de documento': { step: 2 },
      'duplicidade de cadastro-rg': { step: 2 },
      'duplicidade de cpf': { step: 2 },
      'requerente com duplicidade de certidão': { step: 2 },
      'em divergência': { step: 2 },
      'rgs com duplicidade de certidão': { step: 2 },
      'aguardando resposta de of. externo': { step: 2 },
      'enviado para pca': { step: 2 },

      // Status: Em processo de validação do CPF
      'em processo de validação do cpf': { step: 3 },
      'validação cpf': { step: 3 },

      // Status: Em processo de validação do MJ
      'em processo de validação do mj': { step: 4 },
      mj_pendente_processamento: { step: 4 },
      mj_aguardando_recebimento: { step: 4 },
      mj_recebido: { step: 4 },
      mj_erro_processamento: { step: 4 },

      // Status: Em processo de emissão
      'em processo de emissão': { step: 5 },
      'liberado para impressão': { step: 5 },
      'liberado para reimpressão': { step: 5 },
      emitido: { step: 5 },

      // Status: CIN a caminho do posto de atendimento
      'cin a caminho do posto de atendimento': { step: 6 },
      'enviado ao posto': { step: 6 },

      // Status: Aguardando retirada da CIN
      'aguardando retirada da cin': { step: 7 },
      'entregue ao posto/responsável': { step: 7 },

      // Status: CIN retirado com sucesso
      'cin retirado com sucesso': { step: 8 },
      'finalizado (entregue)': { step: 8 },
      'cin entregue': { step: 8 },

      // Status: Bloqueado, cancelado e anulado
      bloqueado: { step: -1, type: 'blocked' },
      cancelado: { step: -1, type: 'cancelled' },
      anulado: { step: -1, type: 'cancelled' },
    };

    // Normaliza o status recebido e verifica no statusMap
    return statusMap[normalizeStatus] || { step: 1 };
  };

  const processStatus = statusProcesso && determineActiveStep();
  const activeStep = processStatus.step;
  const isCancelled = processStatus.type === 'cancelled';
  const isBlocked = processStatus.type === 'blocked';
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Clone do array stepData para manipulação
  const dynamicStepData = [...stepData];
  if (isCancelled) {
    dynamicStepData[dynamicStepData.length - 1] = 'Processo cancelado';
  } else if (isBlocked) {
    dynamicStepData[dynamicStepData.length - 1] = 'Processo bloqueado';
  }

  return (
    <Box className={classes.root}>
      <Stepper
        alternativeLabel={!isMobile}
        orientation={isMobile ? 'vertical' : 'horizontal'}
        activeStep={isCancelled || isBlocked ? dynamicStepData.length : activeStep}
        connector={(
          <ColorlibConnector
            isCancelled={isCancelled}
            isBlocked={isBlocked}
            isMobile={isMobile}
          />
        )}
        className={classes.stepper}
      >
        {dynamicStepData.map((label, index) => (
          <Step key={label} completed={!isCancelled && !isBlocked && index < activeStep}>
            <StepLabel
              classes={{
                label: `${classes.stepLabel} MuiStepLabel-active MuiStepLabel-completed`,
              }}
              StepIconComponent={(props) => (
                <ColorlibStepIcon
                  {...props}
                  isCancelled={isCancelled}
                  isBlocked={isBlocked}
                  isLastStep={index === dynamicStepData.length - 1}
                />
              )}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {(statusProcesso === 'cancelado' || statusProcesso === 'anulado') && (
        <>
          <Typography
            variant="body1"
            className={classes.cancelMessage}
          >
            O processo de emissão do CIN foi cancelado!
          </Typography>
          <Typography
            variant="body1"
            className={classes.cancelMessage}
          >
            Para maiores informações, se dirija ao Posto de Atendimento onde o processo foi iniciado.
          </Typography>
        </>
      )}

      {(statusProcesso === 'bloqueado') && (
        <>
          <Typography
            variant="body1"
            className={classes.cancelMessage}
          >
            O processo de emissão do CIN foi bloqueado!
          </Typography>
          <Typography
            variant="body1"
            className={classes.cancelMessage}
          >
            Para maiores informações, se dirija ao Posto de Atendimento onde o processo foi iniciado.
          </Typography>
        </>
      )}

      {(statusProcesso === 'finalizado (entregue)') && (
      <>
        <Typography
          variant="body1"
          className={classes.cancelMessage}
        >
          CIN foi entregue com sucesso!
        </Typography>
      </>
      )}
    </Box>
  );
}
